import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useSaveIntegration, useTestIntegration } from '@/api/hooks'
import type { IntegrationAccount } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { ErrorBanner, formatDateTime } from '@/components/common'

/**
 * One outside account: what it needs, whether it is on, and whether it works.
 *
 * <p>The boxes are drawn from what the server says the account consists of, so
 * a provider that gains a setting gains a box without this file being touched
 * and without the two drifting apart.</p>
 *
 * <p>A password that is already saved shows as dots. Leaving the dots alone
 * leaves the password alone - opening the form to correct a port number does
 * not quietly blank the account.</p>
 */
export function IntegrationCard({ account }: { account: IntegrationAccount }) {
  const canWrite = useAuthStore((state) => state.canWrite)
  const writable = canWrite('config')
  const save = useSaveIntegration()
  const test = useTestIntegration()

  const [enabled, setEnabled] = useState(account.enabled)
  const [values, setValues] = useState<Record<string, string>>(account.values)
  const [target, setTarget] = useState('')
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setEnabled(account.enabled)
    setValues(account.values)
    setDirty(false)
  }, [account])

  const set = (name: string, value: string) => {
    setValues((current) => ({ ...current, [name]: value }))
    setDirty(true)
  }

  /**
   * A Meta account pointed at somebody who is not Meta.
   *
   * <p>The commonest way to get WhatsApp wrong here, and it answers as an error
   * page or a timeout. Saying so is not enough - the person reading it has an
   * address in the wrong box and a switch to find - so it is offered as the one
   * thing to press.</p>
   */
  const pointedElsewhere =
    account.provider === 'WHATSAPP'
    && values.viaGateway !== 'true'
    && Boolean(values.baseUrl)
    && !values.baseUrl.includes('facebook.com')
    && !values.baseUrl.includes('meta.com')

  const moveToReseller = () => {
    setValues((current) => ({
      ...current,
      viaGateway: 'true',
      // Carried across rather than retyped, and the Meta address put back to
      // what it was before somebody pasted a panel into it.
      endpoint: current.endpoint?.trim() ? current.endpoint : current.baseUrl,
      baseUrl: 'https://graph.facebook.com/v21.0',
    }))
    setDirty(true)
  }

  const testable = account.complete && enabled && !dirty
  const result = test.data

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1">{account.label}</Typography>
          {account.provider === 'EINVOICE' ? null : account.enabled && account.complete ? (
            <Chip size="small" color="success" label="On" />
          ) : account.enabled ? (
            <Chip size="small" color="warning" label="On, but not finished" />
          ) : (
            <Chip size="small" variant="outlined" label="Off" />
          )}
          {account.lastTestedAt ? (
            <Chip
              size="small"
              variant="outlined"
              color={account.lastTestOk ? 'success' : 'error'}
              label={`${account.lastTestOk ? 'Tested' : 'Test failed'} ${formatDateTime(account.lastTestedAt)}`}
            />
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block">
          {account.description}
        </Typography>

        <ErrorBanner error={save.error ?? test.error} />

        {pointedElsewhere ? (
          <Alert
            severity="warning"
            sx={{ mt: 1 }}
            action={
              writable ? (
                <Button color="inherit" size="small" onClick={moveToReseller}>
                  Move it across
                </Button>
              ) : undefined
            }
          >
            {values.baseUrl} is not Meta. If WhatsApp is bought through a reseller, this belongs in
            the gateway address — and the number and the text have to be written into it as{' '}
            {'{to}'} and {'{message}'}.
          </Alert>
        ) : null}

        {/* The e-invoice login is switched on and off in the E-invoice settings
            below, the one place that decides whether invoices are filed. */}
        {account.provider === 'EINVOICE' ? null : (
          <FormControlLabel
            sx={{ mt: 1, display: 'flex' }}
            control={
              <Switch
                checked={enabled}
                disabled={!writable}
                onChange={(event) => {
                  setEnabled(event.target.checked)
                  setDirty(true)
                }}
              />
            }
            label={`Send through ${account.label.toLowerCase()}`}
          />
        )}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Only the half that applies. An account can be two accounts under
              one name - WhatsApp held with Meta, or bought through a reseller -
              and a form showing both sets at once is a form where somebody
              fills in the wrong half and the test fails for a reason nothing
              on the screen explains. */}
          {account.fields
            .filter(
              (field) =>
                !field.dependsOn
                || (values[field.dependsOn] ?? 'false') === field.dependsOnValue,
            )
            .map((field) => (
            <Grid item xs={12} sm={field.type === 'BOOLEAN' ? 4 : 6} key={field.name}>
              {field.type === 'BOOLEAN' ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={values[field.name] === 'true'}
                      disabled={!writable}
                      onChange={(event) => set(field.name, String(event.target.checked))}
                    />
                  }
                  label={field.label}
                />
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  required={field.required}
                  type={field.type === 'PASSWORD' ? 'password' : 'text'}
                  value={values[field.name] ?? ''}
                  disabled={!writable}
                  onChange={(event) => set(field.name, event.target.value)}
                  helperText={field.help}
                  // A saved password shows as dots; clicking into it clears
                  // them, so what is typed is what is meant.
                  onFocus={(event) => {
                    if (field.secret && event.target.value.startsWith('•')) set(field.name, '')
                  }}
                />
              )}
            </Grid>
            ))}
        </Grid>

        {writable ? (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
            {account.provider !== 'EINVOICE' ? (
              <>
                <TextField
                  size="small"
                  label={
                    account.provider === 'EMAIL' ? 'Test address'
                      : account.provider === 'GSTIN' ? 'Test GSTIN'
                      : 'Test number'
                  }
                  value={target}
                  onChange={(event) => setTarget(event.target.value)}
                  placeholder={
                    account.provider === 'EMAIL' ? 'you@example.com'
                      : account.provider === 'GSTIN' ? '29AAICP2912R1ZR'
                      : '9840000000'
                  }
                  sx={{ minWidth: 220 }}
                />
                <Button
                  disabled={!testable || test.isPending || !target}
                  onClick={() => test.mutate({ provider: account.provider, target })}
                >
                  {test.isPending
                    ? (account.provider === 'GSTIN' ? 'Looking up…' : 'Sending…')
                    : (account.provider === 'GSTIN' ? 'Look it up' : 'Send a test')}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {dirty
                    ? 'Save first, then test'
                    : !account.complete
                      ? 'Fill in everything marked required'
                      : !enabled
                        ? 'Switch it on to test'
                        : 'A real message, sent through this account'}
                </Typography>
              </>
            ) : (
              <Box sx={{ flexGrow: 1 }} />
            )}
            <Button
              disabled={!dirty}
              onClick={() => {
                setEnabled(account.enabled)
                setValues(account.values)
                setDirty(false)
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              disabled={!dirty || save.isPending}
              onClick={() =>
                save.mutate(
                  { provider: account.provider, enabled, values },
                  { onSuccess: () => setDirty(false) },
                )
              }
            >
              Save
            </Button>
          </Stack>
        ) : null}

        {result ? (
          <Alert severity={result.ok ? 'success' : 'error'} sx={{ mt: 2 }}>
            {result.message}
          </Alert>
        ) : account.lastTestNote && !account.lastTestOk ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Last test failed: {account.lastTestNote}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}
